'use client';

/**
 * Mobile Menu - Exactly like Vercel Commerce
 * Uses @headlessui/react for transitions
 */

import { Dialog, Transition } from '@headlessui/react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useThemePath } from '@/themes/core/hooks';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Search, { SearchSkeleton } from './Search';

interface MenuItem {
    title: string;
    path: string;
    children?: MenuItem[];
}

export default function MobileMenu({ menu, direction = 'auto', searchPlaceholder }: { menu: MenuItem[], direction?: string, searchPlaceholder?: string }) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const { getLink } = useThemePath();

    // Side based on direction
    const side = direction === 'rtl' ? 'right' : 'left';

    const openMobileMenu = () => setIsOpen(true);
    const closeMobileMenu = () => setIsOpen(false);

    // Close on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setIsOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    // Close on navigation
    useEffect(() => {
        setIsOpen(false);
    }, [pathname, searchParams]);

    return (
        <>
            <button
                onClick={openMobileMenu}
                aria-label="فتح القائمة"
                className="flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground transition-colors md:hidden"
            >
                <Bars3Icon className="h-4" />
            </button>
            <Transition show={isOpen}>
                <Dialog onClose={closeMobileMenu} className="relative z-50">
                    <Transition.Child
                        as={Fragment}
                        enter="transition-all ease-in-out duration-300"
                        enterFrom="opacity-0 backdrop-blur-none"
                        enterTo="opacity-100 backdrop-blur-[.5px]"
                        leave="transition-all ease-in-out duration-200"
                        leaveFrom="opacity-100 backdrop-blur-[.5px]"
                        leaveTo="opacity-0 backdrop-blur-none"
                    >
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-all ease-in-out duration-300"
                        enterFrom={side === 'right' ? 'translate-x-[100%]' : '-translate-x-[100%]'}
                        enterTo="translate-x-0"
                        leave="transition-all ease-in-out duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo={side === 'right' ? 'translate-x-[100%]' : '-translate-x-[100%]'}
                    >
                        <Dialog.Panel
                            className={`fixed bottom-0 ${side === 'left' ? 'left-0' : 'right-0'} top-0 flex h-full w-full flex-col bg-background pb-6 max-w-[300px] shadow-2xl`}
                            dir={direction === 'auto' ? undefined : (direction as any)}
                        >
                            <div className="p-4">
                                <button
                                    className="mb-4 flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground transition-colors"
                                    onClick={closeMobileMenu}
                                    aria-label="إغلاق القائمة"
                                >
                                    <XMarkIcon className="h-6" />
                                </button>

                                <div className="mb-6 w-full px-2">
                                    <Suspense fallback={<SearchSkeleton />}>
                                        <Search direction={direction} placeholder={searchPlaceholder} />
                                    </Suspense>
                                </div>

                                {menu.length ? (
                                    <ul className="flex w-full flex-col gap-1 px-2">
                                        {menu.map((item) => (
                                            <MobileMenuItem key={item.title} item={item} getLink={getLink} closeMenu={closeMobileMenu} />
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        </>
    );
}

function MobileMenuItem({ item, getLink, closeMenu }: { item: MenuItem, getLink: (p: string) => string, closeMenu: () => void }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = item.children && item.children.length > 0;

    return (
        <li className="flex flex-col">
            <div className="flex items-center justify-between group">
                <Link
                    href={getLink(item.path)}
                    prefetch={true}
                    onClick={closeMenu}
                    className="flex-1 py-3 text-lg font-medium text-foreground transition-colors hover:text-primary"
                >
                    {item.title}
                </Link>
                {hasChildren && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            setIsExpanded(!isExpanded);
                        }}
                        className="p-3 text-muted-foreground transition-transform duration-200"
                        aria-label={isExpanded ? "طي" : "توسيع"}
                    >
                        <ChevronDownIcon className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className="flex flex-col border-r-2 border-primary/10 mr-4 mt-1 mb-2">
                    {item.children!.map((child) => (
                        <li key={child.title}>
                            <Link
                                href={getLink(child.path)}
                                prefetch={true}
                                onClick={closeMenu}
                                className="block py-2.5 px-4 text-base text-muted-foreground hover:text-foreground transition-colors border-r-2 border-transparent hover:border-primary"
                            >
                                {child.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}
